import { Factory, Loader2 } from 'lucide-react';
import { useUserFactories } from '@/core/hooks/useUserFactories';
import { useFactoryStore, useFactoryChange } from '@/core/hooks/useFactory';
import { useTenantStore } from '@/core/hooks/useTenant';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Badge } from '@/shared/ui/badge';

export const FactorySelector = () => {
  const { selectedTenantId } = useTenantStore();
  const { selectedFactoryId, isTransitioning } = useFactoryStore();
  const { changeFactory } = useFactoryChange();
  const { data: userFactories = [], isLoading } = useUserFactories();

  const factories = userFactories.filter((factory) => factory.status === 'approved');

  if (!selectedTenantId) {
    return (
      <Badge variant="secondary" className="text-xs">
        Select tenant first
      </Badge>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  if (factories.length === 0) {
    return (
      <Badge variant="secondary" className="text-xs">
        No factories assigned
      </Badge>
    );
  }

  const selectedFactory = factories.find((factory) => factory.factory_id === selectedFactoryId);

  return (
    <Select value={selectedFactoryId || ''} onValueChange={changeFactory} disabled={isTransitioning}>
      <SelectTrigger className="w-[200px] bg-sidebar-accent border-sidebar-border">
        <div className="flex items-center gap-2">
          {isTransitioning ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          ) : (
            <Factory className="h-4 w-4 text-primary" />
          )}
          <SelectValue placeholder="Select factory">
            {selectedFactory?.factory?.name || 'Select factory'}
          </SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent>
        {factories.map((factory) => (
          <SelectItem key={factory.factory_id} value={factory.factory_id}>
            <div className="flex items-center gap-2">
              <span>{factory.factory?.name}</span>
              <Badge variant="outline" className="text-xs">
                {factory.factory?.code}
              </Badge>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
